import { useI18n } from "@/context/I18nContext";
import MapView from "@/components/MapView";
import { CloudRain, Sun, Thermometer, Snowflake, Layers, MapPin, Mountain, Sprout, Flower2, ArrowRight } from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, LineChart, Line, CartesianGrid,
} from "recharts";

export default function LocationReportView({ report }) {
  const { t } = useI18n();
  if (!report) return null;
  const { coordinates, address, elevation_m, climate, hardiness_zone, soil, geology } = report;
  const monthNames = ["Jan", "Feb", "Mär", "Apr", "Mai", "Jun", "Jul", "Aug", "Sep", "Okt", "Nov", "Dez"];
  const chartData = (climate?.monthly || []).map((m) => ({
    month: monthNames[m.month - 1],
    precipitation: m.precipitation_mm,
    temperature: m.temperature_c,
    sunshine: m.sunshine_hours,
  }));

  return (
    <div className="space-y-6" data-testid="location-report-view">
      {/* OVERVIEW & MAP */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white border border-[#E5E2D9] rounded-xl p-6">
          <SectionTitle icon={MapPin}>{t("query.section_overview")}</SectionTitle>
          <dl className="space-y-3 text-sm">
            <Field label={t("query.coords")} value={`${coordinates.latitude.toFixed(5)}, ${coordinates.longitude.toFixed(5)}`} />
            <Field label="Adresse" value={address?.display_name || "—"} multiline />
            <Field label={t("query.country")} value={address?.country || "—"} />
            <Field label={t("query.state")} value={address?.state || "—"} />
            <Field label={t("query.elevation")} value={elevation_m != null ? `${Math.round(elevation_m)} m` : "—"} icon={Mountain} />
          </dl>
        </div>
        <MapView lat={coordinates.latitude} lng={coordinates.longitude} height={340} />
      </div>

      <DurchgebluehtBanner soil={soil} climate={climate} />

      {/* HARDINESS ZONE FEATURE */}
      <div className="bg-[#1A2F24] text-white rounded-xl p-6 md:p-8 relative overflow-hidden">
        <div className="absolute inset-0 grain-bg opacity-20" />
        <div className="relative grid grid-cols-1 md:grid-cols-3 gap-6 items-center">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-white/60 mb-2">{t("query.section_hardiness")}</div>
            <div className="font-display font-bold text-6xl md:text-7xl tracking-tighter" data-testid="hardiness-zone-display">
              {hardiness_zone?.zone || "—"}
            </div>
            {hardiness_zone?.description && (
              <div className="text-sm text-white/70 mt-1">{hardiness_zone.description}</div>
            )}
          </div>
          <div className="md:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <DarkField label={t("query.hardiness_label")} value={hardiness_zone?.label || "—"} />
            <DarkField label={t("query.hardiness_range")} value={hardiness_zone?.range_c || "—"} />
            <DarkField label={t("query.hardiness_min")} value={hardiness_zone?.min_temp_c != null ? `${hardiness_zone.min_temp_c} °C` : "—"} />
            <DarkField
              label={t("query.hardiness_lowest")}
              value={hardiness_zone?.lowest_winter_temp_c != null ? `${hardiness_zone.lowest_winter_temp_c} °C` : "—"}
            />
            {hardiness_zone?.usda_mean_annual_minimum_c != null && (
              <DarkField
                label={t("query.hardiness_usda")}
                value={`${hardiness_zone.usda_mean_annual_minimum_c} °C`}
              />
            )}
          </div>
        </div>
        <div className="relative mt-5 pt-4 border-t border-white/10 text-[11px] text-white/55 space-y-1">
          {hardiness_zone?.method && <div>{hardiness_zone.method}</div>}
          {hardiness_zone?.source && (
            <div>
              {t("query.source")}: {hardiness_zone.source}
              {hardiness_zone.years_analyzed ? ` · ${hardiness_zone.years_analyzed} ${t("query.years")}` : ""}
            </div>
          )}
          {hardiness_zone?.elevation_note && (
            <div className="text-amber-200/90">⚠ {hardiness_zone.elevation_note}</div>
          )}
          {hardiness_zone?.reduced_accuracy && (
            <div className="text-amber-200/90">⚠ {t("query.hardiness_fallback")}</div>
          )}
        </div>
      </div>

      {/* CLIMATE KPIs */}
      <div>
        <SectionTitle icon={CloudRain}>{t("query.section_climate")}</SectionTitle>
        <div className="text-xs text-[#5C6B61] mb-4">{climate?.period}</div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <Kpi icon={Thermometer} label={t("query.annual_temp")} value={climate?.annual_mean_temp_c != null ? `${climate.annual_mean_temp_c} °C` : "—"} />
          <Kpi icon={CloudRain} label={t("query.annual_precip")} value={climate?.annual_precipitation_mm != null ? `${climate.annual_precipitation_mm} mm` : "—"} />
          <Kpi icon={Sun} label={t("query.annual_sun")} value={climate?.annual_sunshine_hours != null ? `${climate.annual_sunshine_hours} h` : "—"} />
          <Kpi icon={Snowflake} label={t("query.frost_days")} value={climate?.frost_days_per_year != null ? `${climate.frost_days_per_year}` : "—"} />
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="bg-white border border-[#E5E2D9] rounded-xl p-5">
            <div className="text-sm font-medium text-[#1A2F24] mb-3">Niederschlag (mm/Monat)</div>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E2D9" />
                <XAxis dataKey="month" stroke="#5C6B61" fontSize={11} />
                <YAxis stroke="#5C6B61" fontSize={11} />
                <Tooltip contentStyle={{ background: "#1A2F24", border: "none", borderRadius: 6, color: "#fff", fontSize: 12 }} />
                <Bar dataKey="precipitation" fill="#6B9E9E" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="bg-white border border-[#E5E2D9] rounded-xl p-5">
            <div className="text-sm font-medium text-[#1A2F24] mb-3">Ø Temperatur (°C/Monat)</div>
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E2D9" />
                <XAxis dataKey="month" stroke="#5C6B61" fontSize={11} />
                <YAxis stroke="#5C6B61" fontSize={11} />
                <Tooltip contentStyle={{ background: "#1A2F24", border: "none", borderRadius: 6, color: "#fff", fontSize: 12 }} />
                <Line type="monotone" dataKey="temperature" stroke="#C05A44" strokeWidth={2.5} dot={{ r: 3, fill: "#C05A44" }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* SOIL */}
      <div className="bg-white border border-[#E5E2D9] rounded-xl p-6">
        <SectionTitle icon={Layers}>{t("query.section_soil")}</SectionTitle>
        {soil?.available ? (
          <>
            <div className="mb-4 rounded-lg bg-[#1A2F24]/5 border border-[#E5E2D9] px-4 py-3">
              <div className="flex items-center gap-2">
                <Sprout size={16} className="text-[#5C6B61] shrink-0" />
                <span className="font-display font-semibold text-[#1A2F24]">{soil.texture_class || "—"}</span>
              </div>
              {soil.texture_description && (
                <p className="text-sm text-[#5C6B61] mt-1">{soil.texture_description}</p>
              )}
            </div>
            <TextureBar sand={soil.sand_percent} silt={soil.silt_percent} clay={soil.clay_percent} t={t} />
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-5">
              <Kpi label={t("query.ph")} value={soil.ph ?? "—"} hint={soil.ph_assessment} />
              <Kpi label={t("query.humus")} value={soil.humus_percent != null ? `${soil.humus_percent} %` : "—"} hint={soil.organic_carbon_g_kg != null ? `Corg ${soil.organic_carbon_g_kg} g/kg` : null} />
              <Kpi label={t("query.cec")} value={soil.cec_cmol_kg != null ? `${soil.cec_cmol_kg}` : "—"} hint={soil.cec_assessment} />
              <Kpi label={t("query.available_water")} value={soil.available_water_vol_percent != null ? `${soil.available_water_vol_percent} Vol-%` : "—"} hint={soil.available_water_mm_0_15cm != null ? `${soil.available_water_mm_0_15cm} mm in 0–15 cm` : null} />
              <Kpi label={t("query.coarse_fragments")} value={soil.coarse_fragments_percent != null ? `${soil.coarse_fragments_percent} %` : "—"} />
              <Kpi label={t("query.nitrogen")} value={soil.nitrogen_g_kg != null ? `${soil.nitrogen_g_kg} g/kg` : "—"} />
              <Kpi label={t("query.bulk_density")} value={soil.bulk_density_kg_dm3 != null ? `${soil.bulk_density_kg_dm3} kg/dm³` : "—"} />
              <Kpi label={t("query.field_capacity")} value={soil.field_capacity_vol_percent != null ? `${soil.field_capacity_vol_percent} Vol-%` : "—"} />
            </div>
            {soil.offset_m > 0 && (
              <div className="mt-4 flex items-start gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2.5">
                <span className="mt-0.5 shrink-0">⚠</span>
                <span>{soil.offset_note}</span>
              </div>
            )}
          </>
        ) : (
          <>
            <div className="text-sm text-[#5C6B61]">{t("query.soil_unavailable")}: {soil?.reason}</div>
            {soil?.association && (
              <div
                className="mt-4 rounded-lg bg-[#1A2F24]/5 border border-[#E5E2D9] px-4 py-3"
                data-testid="soil-association"
              >
                <div className="text-xs uppercase tracking-wider text-[#5C6B61] mb-1.5">
                  {t("query.association_title")}
                </div>
                <div className="flex items-start gap-2">
                  <Sprout size={16} className="text-[#5C6B61] shrink-0 mt-0.5" />
                  <span className="font-display font-semibold text-[#1A2F24]">
                    {soil.association.name}
                    {soil.association.unit && (
                      <span className="font-normal text-[#5C6B61]"> · Einheit {soil.association.unit}</span>
                    )}
                  </span>
                </div>
                {soil.association.description && (
                  <p className="text-sm text-[#5C6B61] mt-2 leading-relaxed">
                    {soil.association.description}
                  </p>
                )}
                <p className="text-xs text-[#5C6B61] mt-3 italic">{t("query.association_note")}</p>
              </div>
            )}
          </>
        )}
        <div className="text-[10px] text-[#5C6B61] mt-4">
          {soil?.source || soil?.association?.attribution}
        </div>
      </div>

      {/* GEOLOGY / UNTERGRUND */}
      {geology?.available && (
        <div className="bg-white border border-[#E5E2D9] rounded-xl p-6" data-testid="geology-section">
          <SectionTitle icon={Mountain}>{t("query.section_geology")}</SectionTitle>
          <dl className="space-y-3 text-sm">
            <Field label={t("query.lithology")} value={geology.lithology || "—"} multiline />
            {geology.unit_name && <Field label={t("query.geo_unit")} value={geology.unit_name} multiline />}
            <Field
              label={t("query.geo_period")}
              value={[geology.period, geology.age_ma ? `${geology.age_ma[0]}–${geology.age_ma[1]} Mio. Jahre` : null].filter(Boolean).join(" · ") || "—"}
            />
            {geology.environment && <Field label={t("query.geo_environment")} value={geology.environment} multiline />}
            {geology.note && <Field label={t("query.geo_note")} value={geology.note} multiline />}
          </dl>
          <div className="text-[10px] text-[#5C6B61] mt-4">{geology.source}</div>
        </div>
      )}

      {/* Data sources note */}
      <div className="text-[10px] text-[#5C6B61] flex items-center gap-1.5 px-1 pt-2" data-testid="data-sources-note">
        <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#1A2F24]" />
        Datenquellen: ERA5-Land (Winterhärte) · NASA POWER (Klima) · ISRIC SoilGrids (Boden) · Macrostrat (Geologie) · OpenStreetMap · Open-Elevation
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Shopware property option IDs for durchgeblueht.de/STAUDEN-MIX filters.
// Find them in Shopware Admin → Katalog → Eigenschaften, or by clicking each
// filter option in the browser and reading the `properties` URL parameter.
// ---------------------------------------------------------------------------
const DGBT_IDS = {
  bodenart: {
    leicht:             "TODO_leicht",
    "leicht/mittelschwer": "TODO_leicht_mittel",
    mittelschwer:       "TODO_mittel",
    schwer:             "TODO_schwer",
  },
  wasser: {
    trocken:            "TODO_trocken",
    "trocken/frisch":   "TODO_trocken_frisch",
    frisch:             "TODO_frisch",
    feucht:             "TODO_feucht",
  },
};

function deriveBodenart(soil) {
  if (!soil?.available) return null;
  const sand = soil.sand_percent ?? 0;
  const clay = soil.clay_percent ?? 0;
  if (sand >= 60) return "leicht";
  if (sand >= 40 || clay < 15) return "leicht/mittelschwer";
  if (clay >= 30) return "schwer";
  return "mittelschwer";
}

function deriveWasserbedarf(climate) {
  const mm = climate?.annual_precipitation_mm;
  if (mm == null) return null;
  if (mm < 450) return "trocken";
  if (mm < 600) return "trocken/frisch";
  if (mm < 800) return "frisch";
  return "feucht";
}

function buildDurchgebluehtUrl(soil, climate) {
  const bodenart = deriveBodenart(soil);
  const wasser = deriveWasserbedarf(climate);
  const ids = [
    bodenart && DGBT_IDS.bodenart[bodenart],
    wasser && DGBT_IDS.wasser[wasser],
  ].filter(Boolean).filter((id) => !id.startsWith("TODO_"));

  const base = "https://www.durchgeblueht.de/STAUDEN-MIX/";
  if (ids.length === 0) return base;
  return `${base}?order=mit-bild&properties=${ids.join("%7C")}`;
}

const BODENART_LABEL = {
  "leicht": "leicht (sandig)",
  "leicht/mittelschwer": "leicht bis mittelschwer",
  "mittelschwer": "mittelschwer",
  "schwer": "schwer (tonig)",
};
const WASSER_LABEL = {
  "trocken": "trocken",
  "trocken/frisch": "trocken bis frisch",
  "frisch": "frisch",
  "feucht": "feucht",
};

function DurchgebluehtBanner({ soil, climate }) {
  const bodenart = deriveBodenart(soil);
  const wasser = deriveWasserbedarf(climate);
  const url = buildDurchgebluehtUrl(soil, climate);

  const tags = [
    bodenart && `Boden: ${BODENART_LABEL[bodenart] ?? bodenart}`,
    wasser && `Wasser: ${WASSER_LABEL[wasser] ?? wasser}`,
  ].filter(Boolean);

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="block group"
    >
      <div className="relative overflow-hidden rounded-xl border border-[#C8D9C0] bg-gradient-to-br from-[#EEF4EB] to-[#F9F8F6] px-6 py-5 flex flex-col sm:flex-row items-start sm:items-center gap-4 hover:border-[#1A2F24] transition-colors">
        <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#1A2F24] flex items-center justify-center">
          <Flower2 size={18} className="text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-display font-semibold text-[#1A2F24] text-base leading-snug">
            Die passenden Stauden für diesen Standort finden
          </p>
          <p className="text-[#5C6B61] text-sm mt-0.5">
            Fertig zusammengestellte Pflanzenmischungen auf{" "}
            <span className="font-medium text-[#1A2F24]">durchgeblüht.de</span>
            {tags.length > 0 && " — gefiltert nach Ihren Standortdaten:"}
          </p>
          {tags.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-2">
              {tags.map((tag) => (
                <span
                  key={tag}
                  className="inline-block text-[11px] font-medium px-2 py-0.5 rounded-full bg-[#1A2F24]/10 text-[#1A2F24]"
                >
                  {tag}
                </span>
              ))}
            </div>
          )}
        </div>
        <div className="flex-shrink-0 flex items-center gap-1 text-sm font-medium text-[#1A2F24] group-hover:gap-2 transition-all">
          Jetzt entdecken <ArrowRight size={15} />
        </div>
      </div>
    </a>
  );
}

function SectionTitle({ children, icon: Icon }) {
  return (
    <h2 className="font-display font-semibold text-xl text-[#1A2F24] mb-4 flex items-center gap-2">
      {Icon ? <Icon size={20} className="text-[#5C6B61]" /> : null}
      {children}
    </h2>
  );
}

function Field({ label, value, multiline = false, icon: Icon }) {
  return (
    <div className="flex gap-3 text-sm">
      <dt className="w-28 text-[#5C6B61] flex items-center gap-1.5 shrink-0">{Icon && <Icon size={14} />} {label}</dt>
      <dd className={`text-[#1A2F24] font-medium ${multiline ? "" : "truncate"}`}>{value}</dd>
    </div>
  );
}

function DarkField({ label, value }) {
  return (
    <div className="bg-white/5 rounded-lg p-3 border border-white/10">
      <div className="text-[10px] uppercase tracking-wide text-white/60">{label}</div>
      <div className="text-sm font-medium text-white mt-1">{value}</div>
    </div>
  );
}

function Kpi({ icon: Icon, label, value, hint }) {
  return (
    <div className="bg-[#F1EFE7] rounded-lg p-4 border border-[#E5E2D9]" data-testid={`kpi-${label?.toString().toLowerCase().replace(/\s/g, "-")}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10px] uppercase tracking-wide text-[#5C6B61]">{label}</span>
        {Icon ? <Icon size={14} className="text-[#5C6B61]" /> : null}
      </div>
      <div className="text-lg font-display font-bold text-[#1A2F24] leading-tight">{value}</div>
      {hint ? <div className="text-[10px] text-[#5C6B61] mt-1 leading-snug">{hint}</div> : null}
    </div>
  );
}

// Korngrößenverteilung als gestapelter Balken — macht auf einen Blick
// sichtbar, wovon die Texturklasse kommt.
function TextureBar({ sand, silt, clay, t }) {
  if (sand == null || silt == null || clay == null) return null;
  const parts = [
    { key: "sand", label: t("query.sand"), value: sand, color: "#D8B36A" },
    { key: "silt", label: t("query.silt"), value: silt, color: "#9FB58F" },
    { key: "clay", label: t("query.clay"), value: clay, color: "#8C6A5D" },
  ];
  const total = parts.reduce((sum, p) => sum + p.value, 0) || 1;
  return (
    <div data-testid="texture-bar">
      <div className="flex h-6 rounded-md overflow-hidden border border-[#E5E2D9]">
        {parts.map((p) => (
          <div
            key={p.key}
            style={{ width: `${(p.value / total) * 100}%`, background: p.color }}
            title={`${p.label}: ${p.value} %`}
          />
        ))}
      </div>
      <div className="flex flex-wrap gap-x-5 gap-y-1 mt-2">
        {parts.map((p) => (
          <span key={p.key} className="flex items-center gap-1.5 text-xs text-[#5C6B61]">
            <span className="w-2.5 h-2.5 rounded-sm" style={{ background: p.color }} />
            {p.label} <span className="font-medium text-[#1A2F24]">{p.value} %</span>
          </span>
        ))}
      </div>
    </div>
  );
}
